import { Component, ChangeDetectionStrategy, inject, signal, computed, effect } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {filter} from 'rxjs/operators';
import {InstructorPlanDetails, instructorPlans, instructorPlansFrequency} from '../instructor-plans';
import {createDefaultDialogConfig} from '../../../common/dialog-utils';
import {AppState} from '../../../store';
import {Store} from '@ngrx/store';
import {selectTenantInfo} from '../../../store/selectors';
import {MessagesService} from '../../../shared/services/messages.service';
import {MatButtonModule} from '@angular/material/button';
import {MatRadioModule} from '@angular/material/radio';
import {MatIconModule} from '@angular/material/icon';
import { NgClass } from '@angular/common';
import {toSignal} from '@angular/core/rxjs-interop';


@Component({
    selector: 'change-instructor-plan-dialog',
    templateUrl: "change-instructor-plan-dialog.component.html",
    styleUrls: ["change-instructor-plan-dialog.component.scss",
        "../common-instructor-plan-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatDialogModule, FormsModule, ReactiveFormsModule, MatIconModule, MatRadioModule, NgClass, MatButtonModule]
})
export class ChangeInstructorPlanDialogComponent {
  private dialogRef = inject<MatDialogRef<ChangeInstructorPlanDialogComponent>>(MatDialogRef);
  private fb = inject(FormBuilder);
  private store = inject<Store<AppState>>(Store);
  private messages = inject(MessagesService);

  form = this.fb.group({
    instructorPricingPlan: ["pro", Validators.required],
    instructorPlanFrequency: ["yearly"]
  });

  // State signals
  currentInstructorPlanId = signal<string>(undefined);
  currentInstructorPlanFrequency = signal<string>(undefined);
  currentInstructorPlan = signal<InstructorPlanDetails>(undefined);
  currentInstructorFrequencyDescription = signal<string>(undefined);
  activateTrial = signal<boolean>(false);
  newInstructorPlan = signal<string>(undefined);
  newInstructorPlanFrequency = signal<string>(undefined);
  classBoldMonthly = signal<string>('unBoldRadioButton');
  classBoldYearly = signal<string>('boldRadioButton');
  check = signal<boolean>(false);

  // Derived state from store
  private tenantInfo = toSignal(this.store.select(selectTenantInfo));

  growth = instructorPlans["growth"];
  basic = instructorPlans["basic"];
  pro = instructorPlans["pro"];
  unlimited = instructorPlans["unlimited"];

  constructor() {
    const data = inject(MAT_DIALOG_DATA);

    this.newInstructorPlan.set(data.newInstructorPlan);
    this.newInstructorPlanFrequency.set(data.newInstructorPlanFrequency);
    this.activateTrial.set(data.activateTrial);

    effect(() => {
      const tenant = this.tenantInfo();
      if (!tenant) return;

      this.currentInstructorPlanId.set(tenant?.instructorPricingPlan);
      this.currentInstructorPlanFrequency.set(tenant?.instructorPlanFrequency);
      this.currentInstructorPlan.set(instructorPlans[tenant?.instructorPricingPlan]);

      let freqDescription = instructorPlansFrequency[tenant?.instructorPlanFrequency];
      if (tenant?.instructorPricingPlan === 'growth') {
        freqDescription = '';
      }
      this.currentInstructorFrequencyDescription.set(freqDescription);

      this.form.patchValue({
        instructorPricingPlan: this.newInstructorPlan() ?? (tenant?.instructorPricingPlan != "growth" ? tenant?.instructorPricingPlan : "pro"),
        instructorPlanFrequency: this.newInstructorPlanFrequency() ?? (tenant?.instructorPlanFrequency ?? 'yearly')
      });
    });
  }

  radioButtonClass() {
    if (this.check()) {
      this.classBoldYearly.set('boldRadioButton');
      this.classBoldMonthly.set('unBoldRadioButton');
      this.check.set(false);
    } else {
      this.classBoldYearly.set('unBoldRadioButton');
      this.classBoldMonthly.set('boldRadioButton');
      this.check.set(true);
    }
  }

  close() {
    this.dialogRef.close();
  }

  get isFreePlanSelected() {
    return this.form.controls["instructorPricingPlan"]?.value == 'growth';
  }

  pricingDetails(instructorPricingPlan: string) {
    const selectedPlanFrequency = this.form.controls['instructorPlanFrequency']?.value;

    const yearlyPlanPrice = instructorPlans[instructorPricingPlan]?.pricingDetails?.yearly
      .split(/ (.*)/)[1];

    return {
      monthly: instructorPlans[instructorPricingPlan]?.pricingDetails[selectedPlanFrequency],
      yearly: yearlyPlanPrice?.slice(1, yearlyPlanPrice?.length - 1)
    };
  }

  get selectedPlan() {
    return this.form.controls["instructorPricingPlan"]?.value;
  }

  get selectedFrequency() {
    return this.form.controls['instructorPlanFrequency']?.value;
  }

  changePlan() {
    const selectedInstructorPlanId = this.selectedPlan,
      selectedPlanFrequency = this.selectedFrequency;

    this.dialogRef.close({
      selectedInstructorPlanId,
      selectedPlanFrequency
    });
  }

  isCurrentPlanSelected() {
    if (this.selectedPlan == 'growth' && this.currentInstructorPlanId() == 'growth') {
      return true;
    }

    return this.currentInstructorPlanId() == this.selectedPlan && this.currentInstructorPlanFrequency() == this.selectedFrequency;
  }
}


export function createInstructorPlanDialog(
  dialog: MatDialog,
  newInstructorPlan?: string,
  newInstructorPlanFrequency?: string,
  activateTrial?: boolean) {

  const dialogConfig = createDefaultDialogConfig();

  dialogConfig.minWidth = '390px';

  dialogConfig.data = {
    newInstructorPlan,
    newInstructorPlanFrequency,
    activateTrial
  };

  const dialogRef = dialog.open(ChangeInstructorPlanDialogComponent, dialogConfig);

  return dialogRef.afterClosed()
    .pipe(
      filter(val => !!val)
    );
}
